import { useState } from "react";
import { toast } from "sonner";
import { GraduationCap, Briefcase, Clapperboard, Sparkles, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";

const TEMPLATES = [
  {
    id: "study",
    name: "Study",
    icon: GraduationCap,
    tint: "from-sky-400 to-violet-500",
    desc: "Notes, revision reminders, daily facts & study bookmarks.",
    chips: ["📖 Tasks", "📚 Reminder", "💡 Learning", "🔖 Bookmarks"],
  },
  {
    id: "work",
    name: "Work",
    icon: Briefcase,
    tint: "from-emerald-400 to-cyan-500",
    desc: "Sprint tasks, meeting notes, standup reminder & news.",
    chips: ["🚀 Tasks", "📝 Notes", "☕ Standup", "📰 News"],
  },
  {
    id: "creator",
    name: "Creator",
    icon: Clapperboard,
    tint: "from-pink-500 to-orange-400",
    desc: "Content pipeline, idea dump, trend feed & post schedule.",
    chips: ["🎬 Pipeline", "💡 Ideas", "📡 Feed", "🗓 Schedule"],
  },
];

export default function TemplatePicker({ onDone }) {
  const [picked, setPicked] = useState(null);
  const [loading, setLoading] = useState(false);

  const apply = async () => {
    if (!picked || loading) return;
    setLoading(true);
    try {
      await api.post("/board-template", { template: picked });
      toast.success("Your board is ready ✦");
      onDone();
    } catch (e) {
      toast.error(apiError(e));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" data-testid="template-picker-modal">
      <div className="w-full max-w-2xl rounded-3xl border border-border bg-card p-6 shadow-2xl card-pop sm:p-8">
        <div className="mb-1 flex items-center gap-2 font-display text-2xl font-extrabold tracking-tight">
          <Sparkles className="h-6 w-6 text-violet-500" />
          <span className="bg-gradient-to-r from-violet-500 to-pink-500 bg-clip-text text-transparent">Pick your board</span>
        </div>
        <p className="mb-6 text-sm text-muted-foreground">Choose a starting layout — we'll set up your canvas with ready-made cards. You can change everything later.</p>
        <div className="grid gap-3 sm:grid-cols-3">
          {TEMPLATES.map((t) => (
            <button key={t.id} data-testid={`template-option-${t.id}`} onClick={() => setPicked(t.id)}
              className={`group flex flex-col items-start rounded-2xl border p-4 text-left transition-all hover:scale-[1.02] ${
                picked === t.id ? "border-violet-500 ring-2 ring-violet-400/50 bg-violet-500/5" : "border-border hover:border-violet-300"}`}>
              <span className={`mb-3 rounded-xl bg-gradient-to-br ${t.tint} p-2.5 text-white shadow-lg`}>
                <t.icon className="h-5 w-5" />
              </span>
              <span className="font-display text-base font-bold">{t.name}</span>
              <span className="mt-1 text-xs text-muted-foreground">{t.desc}</span>
              <span className="mt-3 flex flex-wrap gap-1">
                {t.chips.map((c) => (
                  <span key={c} className="rounded-full bg-secondary px-2 py-0.5 text-[10px]">{c}</span>
                ))}
              </span>
            </button>
          ))}
        </div>
        <button data-testid="template-apply-button" onClick={apply} disabled={!picked || loading}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-violet-500/25 transition-transform enabled:hover:scale-[1.01] enabled:active:scale-95 disabled:opacity-40">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {loading ? "Setting up your board..." : picked ? `Start with ${TEMPLATES.find((t) => t.id === picked).name} ✦` : "Select a template to continue"}
        </button>
      </div>
    </div>
  );
}
